import { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Tab,
  Tabs,
  TextField,
  Stack,
  Typography,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  IconButton,
  InputAdornment
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { openSnackbar } from 'api/snackbar';
import {
  sendAbhaAuthOtp,
  verifyAbhaAuthOtp,
  fetchAbhaProfile,
  downloadAbhaCard,
  findPatientsByMobile,
  findPatientByAbhaNumber,
  formatAbhaProfile
} from 'utils/apiHelper';
import { scoreAbhaMatch } from 'utils/abhaMatchUtils';
import {
  ABHA_ADDRESS_SUFFIX,
  INVALID_OTP_MESSAGE,
  KYC_PENDING_MESSAGE,
  MAX_OTP_RESENDS,
  MAX_RESEND_REACHED_MESSAGE,
  OTP_RESEND_SECONDS,
  extractAbhaErrorMessage,
  isAbhaProfileKycVerified,
  isFailedAuthResult,
  normalizeAbhaNumber,
  blobErrorMessage,
  otpVerificationErrorMessage,
  saveAbhaCardBlob,
  PHR_MOBILE_UPDATE_MESSAGE
} from 'utils/abhaUiUtils';
import AbhaMismatchDialog from 'components/AbhaMismatchDialog';
import AbhaUpdateMobile from 'components/abha/AbhaUpdateMobile';
import AbhaComparison from 'components/abha/AbhaComparison';

function TabPanel({ children, value, index, ...other }) {
  return (
    <div role="tabpanel" hidden={value !== index} {...other}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.number.isRequired,
  value: PropTypes.number.isRequired
};

const extractErrorMessage = extractAbhaErrorMessage;

// "name" or "name@sbx" -> "name@sbx"
const withAbhaSuffix = (value) => {
  const trimmed = String(value || '').trim().toLowerCase();
  if (!trimmed) return '';
  return trimmed.includes('@') ? trimmed : `${trimmed}${ABHA_ADDRESS_SUFFIX}`;
};


const extractTxnId = (response) => response?.txnId || response?.txn_id || '';

/** "91647183121712" -> "91-6471-8312-1712" */
const formatAbhaNumberForDisplay = (value) => {
  const digits = normalizeAbhaNumber(value);
  return digits ? `${digits.slice(0, 2)}-${digits.slice(2, 6)}-${digits.slice(6, 10)}-${digits.slice(10)}` : value || 'N/A';
};

const extractXToken = (response) =>
  response?.tokens?.token || response?.token || response?.xToken || '';

const buildProfileFromAccount = (account) => {
  if (!account || typeof account !== 'object') return null;

  const fullNameStr = account.name || account.fullName || account.firstName || '';
  const fullName = typeof fullNameStr === 'string' ? fullNameStr.trim() : '';
  const parts = fullName ? fullName.split(/\s+/) : [];
  const firstName = parts[0] || '';
  const lastName = parts.length > 1 ? parts.slice(1).join(' ') : '';

  return {
    firstName,
    lastName,
    middleName: '',
    ABHANumber: account.ABHANumber || account.healthIdNumber || account.abhaNumber || '',
    preferredAbhaAddress: account.preferredAbhaAddress || account.abhaAddress || '',
    photo: account.profilePhoto || '',
    mobileVerified: account.mobileVerified ?? null,
    kycVerified: account.kycVerified,
    kycStatus: account.kycStatus
  };
};

export default function AbhaVerification({
  open,
  handleClose,
  openStepper,
  onProfileVerified,
  anchorPatient,
  currentPatient
}) {
  const [tabValue, setTabValue] = useState(0);
  const [aadhaarNumber, setAadhaarNumber] = useState('');
  const [abhaNumber, setAbhaNumber] = useState('');
  const [mobileNumber, setMobileNumber] = useState('');
  const [mobileAuthError, setMobileAuthError] = useState('');
  const [otp, setOtp] = useState('');
  const [showOtp, setShowOtp] = useState(false);
  const [verifyMode, setVerifyMode] = useState('mobile');
  const [showVerifyModes, setShowVerifyModes] = useState(false);
  const [txnId, setTxnId] = useState('');
  const [verificationType, setVerificationType] = useState('mobile');
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [resendTimer, setResendTimer] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [showAadhaar, setShowAadhaar] = useState(false);

  const [abhaAddress, setAbhaAddress] = useState('');

  const [linkedAccounts, setLinkedAccounts] = useState([]);
  const [showAccountSelection, setShowAccountSelection] = useState(false);
  const [tToken, setTToken] = useState('');
  const [verifiedProfile, setVerifiedProfile] = useState(null);
  const [verifiedXToken, setVerifiedXToken] = useState(null);
  const [verifiedResponse, setVerifiedResponse] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Mismatch gate — held here while the receptionist reviews the dialog
  const [pendingProfile, setPendingProfile] = useState(null);
  const [pendingXToken, setPendingXToken] = useState(null);
  const [pendingResponse, setPendingResponse] = useState(null);
  const [showMismatch, setShowMismatch] = useState(false);
  // Mobile flow (M1 obs 31): HMS patients with this mobile are listed first;
  // the chosen ABHA number is then verified by OTP. null = not looked up yet.
  const [hmsMatches, setHmsMatches] = useState(null);
  const [selectedHmsAbha, setSelectedHmsAbha] = useState('');
  const [isLookingUp, setIsLookingUp] = useState(false);
  // M1 iteration 2 obs 8: the HMS patient this ABHA already belongs to
  const [existingPatient, setExistingPatient] = useState(null);
  const [isFindingPatient, setIsFindingPatient] = useState(false);
  // "Go with ABDM" picked: review the ABHA profile (and its mobile) before linking
  const [abdmChosen, setAbdmChosen] = useState(false);

  const resetState = () => {
    setTabValue(0);
    setAadhaarNumber('');
    setAbhaNumber('');
    setAbhaAddress('');
    setMobileNumber('');
    setOtp('');
    setShowOtp(false);
    setVerifyMode('mobile');
    setShowVerifyModes(false);
    setTxnId('');
    setVerificationType('mobile');
    setIsSendingOtp(false);
    setIsVerifyingOtp(false);
    setResendTimer(0);
    setResendCount(0);
    setShowAadhaar(false);
    setLinkedAccounts([]);
    setShowAccountSelection(false);
    setTToken('');
    setVerifiedProfile(null);
    setVerifiedXToken(null);
    setVerifiedResponse(null);
    setIsDownloading(false);
    setPendingProfile(null);
    setPendingXToken(null);
    setPendingResponse(null);
    setShowMismatch(false);
    setHmsMatches(null);
    setSelectedHmsAbha('');
    setIsLookingUp(false);
    setExistingPatient(null);
    setIsFindingPatient(false);
    setAbdmChosen(false);
  };

  useEffect(() => {
    if (!open) {
      resetState();
    }
  }, [open]);

  useEffect(() => {
    if (resendTimer <= 0) return undefined;

    const timerId = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          clearInterval(timerId);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timerId);
  }, [resendTimer]);

  const handleTabChange = (_event, newValue) => {
    setTabValue(newValue);
    setShowOtp(false);
    setShowVerifyModes(false);
    setOtp('');
    setTxnId('');
    setResendTimer(0);
    setResendCount(0);
    setLinkedAccounts([]);
    setShowAccountSelection(false);
    setTToken('');
    setHmsMatches(null);
    setSelectedHmsAbha('');
  };

  const handleCreateNewAbha = () => {
    handleClose();
    openStepper(true);
  };

  const getAuthPayloadForTab = () => {
    if (tabValue === 0) {
      return {
        login_type: 'aadhaar',
        login_id: aadhaarNumber.trim(),
        verification_type: 'aadhaar'
      };
    } else if (tabValue === 1) {
      return {
        login_type: 'abha_number',
        login_id: normalizeAbhaNumber(abhaNumber),
        verification_type: verifyMode === 'aadhaar' ? 'aadhaar' : 'mobile'
      };
    } else if (tabValue === 2) {
      return {
        login_type: 'abha_address',
        login_id: withAbhaSuffix(abhaAddress),
        verification_type: verifyMode === 'aadhaar' ? 'aadhaar' : 'mobile'
      };
    } else if (selectedHmsAbha) {
      // Profile picked from HMS: OTP on the mobile number linked with that ABHA
      return {
        login_type: 'abha_number',
        login_id: selectedHmsAbha,
        verification_type: 'mobile'
      };
    } else {
      return {
        login_type: 'mobile',
        login_id: mobileNumber.trim(),
        verification_type: 'mobile'
      };
    }
  };

  // `override` lets a caller send for state it has just set; as an onClick
  // handler this receives the click event and uses the current tab's payload.
  const handleSendOTP = async (override) => {
    const payload = override?.login_type ? override : getAuthPayloadForTab();
    if (!payload.login_id) return;
    const isResend = showOtp;
    if (isResend && resendCount >= MAX_OTP_RESENDS) {
      openSnackbar({ open: true, message: MAX_RESEND_REACHED_MESSAGE, variant: 'alert', alert: { color: 'warning' } });
      return;
    }

    setIsSendingOtp(true);
    setMobileAuthError('');
    try {
      const response = await sendAbhaAuthOtp(payload);
      const nextTxnId = extractTxnId(response);
      if (!nextTxnId) {
        throw new Error('Transaction id missing from send OTP response');
      }

      setTxnId(nextTxnId);
      setVerificationType(payload.verification_type);
      setShowOtp(true);
      setResendTimer(OTP_RESEND_SECONDS);
      if (isResend) {
        setResendCount((prev) => prev + 1);
      }

      openSnackbar({
        open: true,
        message: response?.message || 'OTP sent successfully',
        variant: 'alert',
        alert: { color: 'success' }
      });
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractErrorMessage(error, 'Failed to send OTP'),
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsSendingOtp(false);
    }
  };

  const finalizeVerification = async (verifyResponse, payload) => {
    // ABDM answers a wrong OTP with HTTP 200 + authResult "failed" and no token/profile.
    if (isFailedAuthResult(verifyResponse)) {
      openSnackbar({ open: true, message: INVALID_OTP_MESSAGE, variant: 'alert', alert: { color: 'error' } });
      return;
    }
    const xToken = extractXToken(verifyResponse);
    let profilePayload = verifyResponse?.ABHAProfile || verifyResponse?.profile;
    const accountPayload = Array.isArray(verifyResponse?.accounts) ? verifyResponse.accounts[0] : (Array.isArray(verifyResponse?.users) ? verifyResponse.users[0] : null);

    if (!profilePayload && accountPayload) {
      profilePayload = buildProfileFromAccount(accountPayload);
    }

    // Fetch full profile if we don't have it or if we only have a partial profile (e.g. abha_address where photo/dob are missing)
    const isPartialProfile = !profilePayload || !profilePayload.photo || !profilePayload.dob;
    if (isPartialProfile && xToken) {
      try {
        const fullProfile = await fetchAbhaProfile(xToken);
        if (fullProfile) {
            profilePayload = { ...(profilePayload || {}), ...fullProfile };
        }
      } catch (profileError) {
        console.error('Failed to fetch ABHA profile after verify:', profileError);
      }
    }

    const formattedProfile = profilePayload ? formatAbhaProfile(profilePayload) : null;

    if (!formattedProfile) {
      openSnackbar({
        open: true,
        message: 'Could not fetch full ABHA profile.',
        variant: 'alert',
        alert: { color: 'error' }
      });
      return;
    }

    // ── Demographic gate ───────────────────────────────────────────────────
    // If the "Link ABHA" flow has a selected anchor patient, check whether
    // the verified ABHA profile plausibly belongs to the same person.
    if (anchorPatient) {
      const { isMatch } = scoreAbhaMatch(formattedProfile, anchorPatient);
      if (!isMatch) {
        // Stash the result; show the mismatch dialog — DO NOT advance to the profile card.
        setPendingProfile(formattedProfile);
        setPendingXToken(xToken);
        setPendingResponse(verifyResponse);
        setShowMismatch(true);
        return;
      }
    }

    // Match confirmed (or no anchor patient — standalone use) → proceed normally
    setVerifiedProfile(formattedProfile);
    setVerifiedXToken(xToken);
    setVerifiedResponse(verifyResponse);

    openSnackbar({
      open: true,
      message: verifyResponse?.message || 'ABHA details fetched successfully',
      variant: 'alert',
      alert: { color: 'success' }
    });
  };

  /**
   * Called when the receptionist explicitly confirms linking despite the mismatch.
   * Moves the stashed pending profile into the "verified" slots and closes the dialog.
   */
  // The verified ABHA is already in HMS when the form is for a patient (edit
  // or booking), or when a patient has this ABHA number: then the comparison
  // screen replaces "Link to HIMS & Proceed".
  const hmsPatient = currentPatient?.id ? currentPatient : anchorPatient?.id ? anchorPatient : null;
  useEffect(() => {
    if (!verifiedProfile) return undefined;
    if (hmsPatient) {
      setExistingPatient(hmsPatient);
      return undefined;
    }
    const abha = normalizeAbhaNumber(verifiedProfile.abhaNumber);
    if (!abha) return undefined;
    let cancelled = false;
    setIsFindingPatient(true);
    findPatientByAbhaNumber(abha)
      .then((match) => {
        if (!cancelled && match?.exists && match.patient) setExistingPatient(match.patient);
      })
      .catch((error) => console.error('HMS lookup by ABHA number failed:', error))
      .finally(() => {
        if (!cancelled) setIsFindingPatient(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [verifiedProfile]);

  // ABHA address (PHR) logins get a token the ABHA mobile-update API rejects.
  const isPhrLogin = tabValue === 2;

  const handleProceed = (choice) => {
    // Self-declared (non-KYC) ABHA profiles must not be registered.
    if (!profileKycVerified) {
      openSnackbar({ open: true, message: KYC_PENDING_MESSAGE, variant: 'alert', alert: { color: 'warning' } });
      return;
    }
    // The verification session, so the patient form can update the ABHA's
    // mobile when it is changed there.
    const abhaSession = {
      xToken: verifiedXToken,
      isPhrLogin,
      abhaNumber: verifiedProfile?.abhaNumber,
      mobile: verifiedProfile?.mobile
    };
    onProfileVerified?.(verifiedProfile, verifiedResponse, choice ? { ...choice, abhaSession } : undefined);
    handleClose();
  };

  const handleMismatchLinkAnyway = () => {
    setVerifiedProfile(pendingProfile);
    setVerifiedXToken(pendingXToken);
    setVerifiedResponse(pendingResponse);
    setPendingProfile(null);
    setPendingXToken(null);
    setPendingResponse(null);
    setShowMismatch(false);
    openSnackbar({
      open: true,
      message: 'Override confirmed — review the profile below before linking.',
      variant: 'alert',
      alert: { color: 'warning' }
    });
  };

  /**
   * Called when the receptionist cancels after seeing the mismatch dialog.
   * Clears the stashed data; user is back at the OTP input to try again.
   */
  const handleMismatchCancel = () => {
    setPendingProfile(null);
    setPendingXToken(null);
    setPendingResponse(null);
    setShowMismatch(false);
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6 || !txnId) return;

    setIsVerifyingOtp(true);
    try {
      const payload = getAuthPayloadForTab();
      const verifyResponse = await verifyAbhaAuthOtp({
        txn_id: txnId,
        otp: otp,
        verification_type: verificationType,
        login_type: payload.login_type
      });

      if (isFailedAuthResult(verifyResponse)) {
        throw new Error(INVALID_OTP_MESSAGE);
      }

      const isDiscoveryFlow = ['mobile'].includes(payload.login_type);
      const accounts = verifyResponse?.accounts || verifyResponse?.users;

      if (isDiscoveryFlow && Array.isArray(accounts)) {
        if (accounts.length === 0) {
          setMobileAuthError("ABHA Profile not found. We did not find any ABHA profile linked to this identifier.");
          setIsVerifyingOtp(false);
          return;
        } else {
          setMobileAuthError('');
        }

        const token = verifyResponse.token || verifyResponse.tokens?.token || '';
        const currentTxnId = verifyResponse.txnId || txnId;

        if (accounts.length === 1) {
          const abhaNumber = accounts[0].ABHANumber || accounts[0].healthIdNumber || accounts[0].abhaNumber;
          const { verifyAbhaMobileUserSelection } = await import('utils/apiHelper');
          const finalResponse = await verifyAbhaMobileUserSelection({
            txn_id: currentTxnId,
            abha_number: abhaNumber,
            t_token: token
          });
          await finalizeVerification(finalResponse, payload);
          return;
        } else {
          setLinkedAccounts(accounts);
          setTToken(token);
          setTxnId(currentTxnId);
          setShowAccountSelection(true);
          setIsVerifyingOtp(false);
          return;
        }
      }

      await finalizeVerification(verifyResponse, payload);
    } catch (error) {
      openSnackbar({
        open: true,
        message: otpVerificationErrorMessage(error),
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const handleVerifyUserSelection = async (abhaNumber) => {
    setIsVerifyingOtp(true);
    try {
      const { verifyAbhaMobileUserSelection } = await import('utils/apiHelper');
      const verifyResponse = await verifyAbhaMobileUserSelection({
        txn_id: txnId,
        abha_number: abhaNumber,
        t_token: tToken
      });
      const payload = getAuthPayloadForTab();
      await finalizeVerification(verifyResponse, payload);
    } catch (error) {
      openSnackbar({
        open: true,
        message: extractErrorMessage(error, 'Failed to verify selected account'),
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsVerifyingOtp(false);
    }
  };

  const abdmMobilePayload = () => ({ login_type: 'mobile', login_id: mobileNumber.trim(), verification_type: 'mobile' });

  /** Mobile flow step 1: list the ABHA numbers HMS has for this mobile; none -> ABDM's mobile login. */
  const handleMobileProceed = async () => {
    setIsLookingUp(true);
    setMobileAuthError('');
    let matches = [];
    try {
      matches = await findPatientsByMobile(mobileNumber.trim());
    } catch (error) {
      console.error('HMS lookup by mobile failed; using ABDM mobile login:', error);
    } finally {
      setIsLookingUp(false);
    }
    if (matches.length) {
      setHmsMatches(matches);
      return;
    }
    setHmsMatches([]);
    await handleSendOTP(abdmMobilePayload());
  };

  /** Mobile flow step 2-3: the user picked one of the HMS profiles -> OTP for that ABHA number. */
  const handleSelectHmsAbha = async (patient) => {
    const abha = normalizeAbhaNumber(patient.abha_number);
    if (!abha) return;
    setSelectedHmsAbha(abha);
    await handleSendOTP({ login_type: 'abha_number', login_id: abha, verification_type: 'mobile' });
  };

  /** The patient's ABHA is not in the HMS list: continue with ABDM's mobile login. */
  const handleMobileNotListed = async () => {
    setSelectedHmsAbha('');
    setHmsMatches([]);
    await handleSendOTP(abdmMobilePayload());
  };

  const handleVerifyAbha = () => {
    if (!abhaNumber.trim()) return;
    setShowVerifyModes(true);
  };

  const canSendAadhaarOtp = aadhaarNumber.trim().length === 12 && !isSendingOtp;
  const canSendAbhaOtp =
    Boolean(normalizeAbhaNumber(abhaNumber)) &&
    verifyMode &&
    !isSendingOtp;
  const resendLabel = resendTimer > 0 ? `Resend OTP in ${resendTimer}s` : 'Resend OTP';
  const resendLimitReached = resendCount >= MAX_OTP_RESENDS;
  const resendHint = resendLimitReached ? (
    <Typography variant="caption" color="error">{MAX_RESEND_REACHED_MESSAGE}</Typography>
  ) : resendTimer > 0 ? (
    <Typography variant="caption" color="text.secondary">
      You can request a new OTP after {resendTimer} second{resendTimer === 1 ? '' : 's'}. Resend attempts left: {MAX_OTP_RESENDS - resendCount}
    </Typography>
  ) : (
    <Typography variant="caption" color="text.secondary">Resend attempts left: {MAX_OTP_RESENDS - resendCount}</Typography>
  );
  const profileKycVerified = isAbhaProfileKycVerified(verifiedProfile);

  const handleDownloadCard = async () => {
    if (!verifiedXToken) return;
    if (!profileKycVerified) {
      openSnackbar({ open: true, message: KYC_PENDING_MESSAGE, variant: 'alert', alert: { color: 'warning' } });
      return;
    }
    setIsDownloading(true);
    try {
      const blob = await downloadAbhaCard(verifiedXToken);
      await saveAbhaCardBlob(blob);
    } catch (err) {
      console.error('Failed to download ABHA card:', err);
      openSnackbar({
        open: true,
        message: await blobErrorMessage(err, 'Failed to download card'),
        variant: 'alert',
        alert: { color: 'error' }
      });
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <>
      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h4">ABHA Verification</Typography>
        <Button variant="contained" color="primary" onClick={handleCreateNewAbha}>
          Create New ABHA
        </Button>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Box sx={{ width: '100%' }}>
          
        {verifiedProfile && existingPatient && !abdmChosen ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>Compare Patient Details</Typography>
            {!profileKycVerified && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {KYC_PENDING_MESSAGE}
              </Typography>
            )}
            <AbhaComparison
              patient={existingPatient}
              profile={verifiedProfile}
              onGoWithAbdm={() => setAbdmChosen(true)}
              onGoWithHims={() => handleProceed({ existingPatient, useAbhaDetails: false })}
            />
            <Button
              variant="text"
              onClick={handleDownloadCard}
              disabled={isDownloading || !verifiedXToken}
              startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : null}
              sx={{ mt: 1 }}
            >
              Download ABHA Card
            </Button>
          </Box>
        ) : verifiedProfile ? (
          <Box sx={{ p: 3 }}>
            <Typography variant="h5" gutterBottom>ABHA Profile Details</Typography>
            <Box sx={{ display: 'flex', gap: 3, mb: 4, mt: 2, alignItems: 'center' }}>
              {verifiedProfile.photo ? (
                <Box
                  component="img"
                  src={verifiedProfile.photo.startsWith('data:') ? verifiedProfile.photo : `data:image/jpeg;base64,${verifiedProfile.photo}`}
                  alt="Profile"
                  sx={{ width: 100, height: 100, borderRadius: '50%', objectFit: 'cover', border: '2px solid #eee' }}
                />
              ) : (
                <Box sx={{ width: 100, height: 100, borderRadius: '50%', bgcolor: 'grey.200', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Typography variant="body2" color="text.secondary">No Photo</Typography>
                </Box>
              )}
              <Box sx={{ flex: 1 }}>
                <Typography variant="h6">{[verifiedProfile.firstName, verifiedProfile.middleName, verifiedProfile.lastName].filter(Boolean).join(' ')}</Typography>
                <Typography variant="body1" color="text.secondary">ABHA No: <strong>{verifiedProfile.abhaNumber || 'N/A'}</strong></Typography>
                <Typography variant="body2" color="text.secondary">ABHA Address: {verifiedProfile.abhaAddress || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary">Gender: {verifiedProfile.gender || 'N/A'} | DOB: {verifiedProfile.dateOfBirth?.format ? verifiedProfile.dateOfBirth.format('YYYY-MM-DD') : (verifiedProfile.dayOfBirth ? `${verifiedProfile.dayOfBirth}-${verifiedProfile.monthOfBirth}-${verifiedProfile.yearOfBirth}` : 'N/A')}</Typography>
                <Typography variant="body2" color="text.secondary">Mobile: {verifiedProfile.mobile || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary">Address: {verifiedProfile.address || 'N/A'}</Typography>
                <Typography variant="body2" color="text.secondary">
                  State: {verifiedProfile.state || 'N/A'} | District: {verifiedProfile.district || 'N/A'} | PIN Code: {verifiedProfile.pincode || 'N/A'}
                </Typography>
              </Box>
            </Box>
            {!profileKycVerified && (
              <Typography variant="body2" color="error" sx={{ mb: 2 }}>
                {KYC_PENDING_MESSAGE}
              </Typography>
            )}
            {abdmChosen && isPhrLogin && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {PHR_MOBILE_UPDATE_MESSAGE}
              </Typography>
            )}
            {(tabValue === 3 || (abdmChosen && !isPhrLogin)) && verifiedXToken && (
              <AbhaUpdateMobile
                xToken={verifiedXToken}
                abhaNumber={verifiedProfile.abhaNumber}
                currentMobile={verifiedProfile.mobile}
                onUpdated={(mobile) => setVerifiedProfile((prev) => ({ ...prev, mobile }))}
              />
            )}
            <Divider sx={{ mb: 3 }} />
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button 
                variant="outlined" 
                onClick={handleDownloadCard}
                disabled={isDownloading || !verifiedXToken}
                startIcon={isDownloading ? <CircularProgress size={18} color="inherit" /> : null}
              >
                Download ABHA Card
              </Button>
              <Button 
                variant="contained" 
                onClick={() => handleProceed(abdmChosen ? { existingPatient, useAbhaDetails: true } : undefined)}
                disabled={isFindingPatient}
              >
                Link to HIMS & Proceed
              </Button>
            </Stack>
          </Box>
        ) : (
          <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
              <Tab label="Aadhaar" />
              <Tab label="ABHA Number" />
              <Tab label="ABHA Address" />
              <Tab label="Mobile Number" />
            </Tabs>
          </Box>

          <TabPanel value={tabValue} index={0}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Aadhaar Number"
                type={showAadhaar ? 'text' : 'password'}
                autoComplete="off"
                value={aadhaarNumber}
                onChange={(e) => setAadhaarNumber(e.target.value.replace(/\D/g, '').slice(0, 12))}
                placeholder="Enter 12 digit Aadhaar number"
                // locked once the OTP is sent: the OTP belongs to this number
                disabled={showOtp}
                inputProps={{ inputMode: 'numeric', maxLength: 12 }}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label={showAadhaar ? 'Mask Aadhaar number' : 'Unmask Aadhaar number'}
                        onClick={() => setShowAadhaar((prev) => !prev)}
                        edge="end"
                      >
                        {showAadhaar ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  )
                }}
              />
              {!showOtp ? (
                <Button
                  variant="contained"
                  onClick={handleSendOTP}
                  disabled={!canSendAadhaarOtp}
                  startIcon={isSendingOtp ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  Send OTP
                </Button>
              ) : (
                <>
                  <TextField
                    fullWidth
                    label="Enter OTP"
                    value={otp}
                    onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="Enter 6 digit OTP"
                    inputProps={{ inputMode: 'numeric' }}
                  />
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="contained"
                      onClick={handleVerifyOTP}
                      disabled={otp.trim().length < 6 || isVerifyingOtp}
                      startIcon={isVerifyingOtp ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      Verify OTP
                    </Button>
                    <Button
                      variant="outlined"
                      onClick={handleSendOTP}
                      disabled={isSendingOtp || resendTimer > 0 || resendLimitReached}
                    >
                      {resendLabel}
                    </Button>
                  </Stack>
                  {resendHint}
                </>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="ABHA Number"
                value={abhaNumber}
                onChange={(e) => setAbhaNumber(e.target.value.replace(/[^\d-]/g, '').slice(0, 17))}
                disabled={showOtp}
                placeholder="Enter 14 digit ABHA number (e.g. 91-1234-5678-9012)"
                error={abhaNumber.trim().length > 0 && !normalizeAbhaNumber(abhaNumber)}
                helperText={abhaNumber.trim().length > 0 && !normalizeAbhaNumber(abhaNumber) ? 'ABHA number must be 14 digits' : ''}
              />
              {!showVerifyModes ? (
                <Button
                  variant="contained"
                  onClick={handleVerifyAbha}
                  disabled={!normalizeAbhaNumber(abhaNumber)}
                >
                  Continue
                </Button>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="h6">Select Verify Mode</Typography>
                  <FormControl component="fieldset">
                    <RadioGroup value={verifyMode} onChange={(e) => setVerifyMode(e.target.value)}>
                      <FormControlLabel
                        value="aadhaar"
                        control={<Radio />}
                        label="OTP on mobile number linked with AADHAAR"
                      />
                      <FormControlLabel
                        value="mobile"
                        control={<Radio />}
                        label="OTP on mobile number linked with ABHA"
                      />
                    </RadioGroup>
                  </FormControl>
                  {!showOtp ? (
                    <Button
                      variant="contained"
                      onClick={handleSendOTP}
                      disabled={!canSendAbhaOtp}
                      startIcon={isSendingOtp ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      Send OTP
                    </Button>
                  ) : (
                    <>
                      <TextField
                        fullWidth
                        label="Enter OTP"
                        value={otp}
                        onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))}
                        placeholder="Enter 6 digit OTP"
                        inputProps={{ inputMode: 'numeric' }}
                      />
                      <Stack direction="row" spacing={1}>
                        <Button
                          variant="contained"
                          onClick={handleVerifyOTP}
                          disabled={otp.trim().length < 6 || isVerifyingOtp}
                          startIcon={isVerifyingOtp ? <CircularProgress size={18} color="inherit" /> : null}
                        >
                          Verify OTP
                        </Button>
                        <Button
                          variant="outlined"
                          onClick={handleSendOTP}
                          disabled={isSendingOtp || resendTimer > 0 || resendLimitReached}
                        >
                          {resendLabel}
                        </Button>
                      </Stack>
                      {resendHint}
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="ABHA Address"
                value={abhaAddress}
                onChange={(e) => setAbhaAddress(e.target.value.trim().toLowerCase().split('@')[0])}
                disabled={showOtp}
                placeholder="name"
                helperText={`Enter only the part before ${ABHA_ADDRESS_SUFFIX} (e.g. name${ABHA_ADDRESS_SUFFIX})`}
                InputProps={{
                  endAdornment: <InputAdornment position="end">{ABHA_ADDRESS_SUFFIX}</InputAdornment>
                }}
              />
              {!showVerifyModes ? (
                <Button
                  variant="contained"
                  onClick={() => {
                    if (abhaAddress.trim()) setShowVerifyModes(true);
                  }}
                  disabled={!abhaAddress.trim()}
                >
                  Continue
                </Button>
              ) : (
                <Stack spacing={2}>
                  <Typography variant="h6">Select Verify Mode</Typography>
                  <FormControl component="fieldset">
                    <RadioGroup value={verifyMode} onChange={(e) => setVerifyMode(e.target.value)}>
                      <FormControlLabel value="aadhaar" control={<Radio />} label="OTP on mobile number linked with AADHAAR" />
                      <FormControlLabel value="mobile" control={<Radio />} label="OTP on mobile number linked with ABHA" />
                    </RadioGroup>
                  </FormControl>
                  {!showOtp ? (
                    <Button
                      variant="contained"
                      onClick={handleSendOTP}
                      disabled={!abhaAddress.trim() || isSendingOtp}
                      startIcon={isSendingOtp ? <CircularProgress size={18} color="inherit" /> : null}
                    >
                      Send OTP
                    </Button>
                  ) : (
                    <>
                      <TextField fullWidth label="Enter OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6 digit OTP" inputProps={{ inputMode: 'numeric' }} />
                      <Stack direction="row" spacing={1}>
                        <Button variant="contained" onClick={handleVerifyOTP} disabled={otp.trim().length < 6 || isVerifyingOtp} startIcon={isVerifyingOtp ? <CircularProgress size={18} color="inherit" /> : null}>Verify OTP</Button>
                        <Button variant="outlined" onClick={handleSendOTP} disabled={isSendingOtp || resendTimer > 0 || resendLimitReached}>{resendLabel}</Button>
                      </Stack>
                      {resendHint}
                    </>
                  )}
                </Stack>
              )}
            </Stack>
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            <Stack spacing={3}>
              <TextField
                fullWidth
                label="Mobile Number"
                value={mobileNumber}
                onChange={(e) => {
                  setMobileNumber(e.target.value.replace(/\D/g, '').slice(0, 10));
                  setHmsMatches(null);
                  setSelectedHmsAbha('');
                }}
                disabled={showOtp || showAccountSelection}
                placeholder="Enter 10 digit mobile number"
                error={mobileNumber.length > 0 && mobileNumber.length < 10}
                helperText={mobileNumber.length > 0 && mobileNumber.length < 10 ? "Please enter a valid 10-digit mobile number" : ""}
              />
              {mobileAuthError && (
                <Box sx={{ p: 2, bgcolor: 'error.lighter', color: 'error.main', borderRadius: 1 }}>
                  <Typography variant="body2">{mobileAuthError}</Typography>
                  {mobileAuthError.includes("ABHA Number not found") && (
                    <Button 
                      variant="outlined" 
                      color="error" 
                      size="small" 
                      sx={{ mt: 1 }}
                      onClick={() => {
                        handleClose();
                        openStepper(0);
                      }}
                    >
                      Create ABHA
                    </Button>
                  )}
                </Box>
              )}
              {!showOtp && !showAccountSelection && hmsMatches?.length ? (
                <Stack spacing={2}>
                  <Typography variant="subtitle1">Select an ABHA Profile</Typography>
                  {hmsMatches.map((patient) => (
                    <Box
                      key={patient.id}
                      sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="subtitle1">{patient.full_name}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          ABHA No: {formatAbhaNumberForDisplay(patient.abha_number)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">ABHA Address: {patient.abha_address || 'N/A'}</Typography>
                      </Box>
                      <Button
                        variant="outlined"
                        size="small"
                        disabled={isSendingOtp}
                        onClick={() => handleSelectHmsAbha(patient)}
                      >
                        Select
                      </Button>
                    </Box>
                  ))}
                  <Button variant="text" onClick={handleMobileNotListed} disabled={isSendingOtp} sx={{ alignSelf: 'flex-start' }}>
                    ABHA not listed? Search ABDM with this mobile number
                  </Button>
                  {isSendingOtp && <CircularProgress size={20} />}
                </Stack>
              ) : !showOtp && !showAccountSelection ? (
                <Button
                  variant="contained"
                  onClick={handleMobileProceed}
                  disabled={mobileNumber.length !== 10 || isSendingOtp || isLookingUp}
                  startIcon={isSendingOtp || isLookingUp ? <CircularProgress size={18} color="inherit" /> : null}
                >
                  Proceed
                </Button>
              ) : showAccountSelection ? (
                <Stack spacing={2}>
                  <Typography variant="subtitle1">Select an ABHA Profile</Typography>
                  {linkedAccounts.map((account, index) => {
                    const dispName = account.name || account.fullName || account.firstName;
                    const dispAbha = account.ABHANumber || account.healthIdNumber || account.abhaNumber;
                    const dispAddress = account.preferredAbhaAddress || account.abhaAddress || 'N/A';
                    const dispPhoto = account.profilePhoto || account.photo || null;
                    return (
                    <Box 
                      key={index}
                      sx={{ p: 2, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                        {dispPhoto ? (
                          <Box
                            component="img"
                            src={dispPhoto.startsWith('data:') ? dispPhoto : `data:image/jpeg;base64,${dispPhoto}`}
                            alt="Profile"
                            sx={{ width: 48, height: 48, borderRadius: '50%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Box sx={{ width: 48, height: 48, borderRadius: '50%', bgcolor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="caption" color="text.secondary">No Photo</Typography>
                          </Box>
                        )}
                        <Box>
                          <Typography variant="subtitle1">{dispName}</Typography>
                          <Typography variant="body2" color="text.secondary">ABHA No: {dispAbha}</Typography>
                          <Typography variant="body2" color="text.secondary">Address: {dispAddress}</Typography>
                        </Box>
                      </Box>
                      <Button 
                        variant="outlined" 
                        size="small"
                        disabled={isVerifyingOtp}
                        onClick={() => handleVerifyUserSelection(dispAbha)}
                      >
                        Select
                      </Button>
                    </Box>
                  )})}
                </Stack>
              ) : (
                <>
                  {selectedHmsAbha && (
                    <Typography variant="body2" color="text.secondary">
                      OTP sent to the mobile number linked with ABHA No {formatAbhaNumberForDisplay(selectedHmsAbha)}
                    </Typography>
                  )}
                  <TextField fullWidth label="Enter OTP" value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} placeholder="Enter 6 digit OTP" inputProps={{ inputMode: 'numeric' }} />
                  <Stack direction="row" spacing={1}>
                    <Button variant="contained" onClick={handleVerifyOTP} disabled={otp.trim().length < 6 || isVerifyingOtp} startIcon={isVerifyingOtp ? <CircularProgress size={18} color="inherit" /> : null}>Verify OTP</Button>
                    <Button variant="outlined" onClick={handleSendOTP} disabled={isSendingOtp || resendTimer > 0 || resendLimitReached}>{resendLabel}</Button>
                  </Stack>
                  {resendHint}
                </>
              )}
            </Stack>
                    </TabPanel>
          </Box>
        )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="inherit">
          Cancel
        </Button>
      </DialogActions>
    </Dialog>

    {/* Demographic mismatch gate — shown when the verified ABHA profile
        does not match the locally-selected patient. */}
    <AbhaMismatchDialog
      open={showMismatch}
      localPatient={anchorPatient}
      abhaProfile={pendingProfile}
      onLinkAnyway={handleMismatchLinkAnyway}
      onCancel={handleMismatchCancel}
    />
    </>
  );
}

AbhaVerification.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  openStepper: PropTypes.func.isRequired,
  onProfileVerified: PropTypes.func,
  /** The currently selected local patient. When provided, demographic
   *  matching is enforced before allowing the ABHA to be linked. */
  anchorPatient: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    full_name: PropTypes.string,
    gender: PropTypes.string,
    dob: PropTypes.string,
  }),
  /** The HMS patient the form is editing; compared with the verified ABHA. */
  currentPatient: PropTypes.object,
};
